import React, { Component } from 'react'
import Button from '@material-ui/core/Button'
import CircularProgress from '@material-ui/core/CircularProgress'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Switch from '@material-ui/core/Switch'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { withTranslation } from 'react-i18next'
import { login, loadSession, clearSession } from '../services/gatewayAuth'
import {
  DEFAULT_CONTROLLER_URL,
  getControllerUrl,
  loadControllerSession,
  clearControllerSession,
  loginWithGoogle,
  fetchMe,
  setControllerUrl,
} from '../services/controllerAuth'
import { subscribeToPush } from '../services/pushService'
import { startPresence, stopPresence } from '../services/gatewayPresence'
import {
  applyControllerLinks,
  loadEntitlements,
  saveControllerLinks,
  parsePlanQueryParam,
} from '../plan'
import { UDV_SURFACE, udvDialogProps } from './udvSurface'

const DEFAULT_GATEWAY_URL = 'http://localhost:8088'
const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client'

function extractClinicName(session) {
  const raw = (session && session.raw) || {}
  return (
    raw.clinic_name ||
    raw.clinicName ||
    (raw.clinic && (raw.clinic.name || raw.clinic)) ||
    raw.organization ||
    raw.org_name ||
    null
  )
}

function getGoogleClientId() {
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_GOOGLE_CLIENT_ID) {
    return String(process.env.REACT_APP_GOOGLE_CLIENT_ID).trim()
  }
  return ''
}

let gisScriptPromise = null

function loadGisScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('GIS no disponible'))
  }
  if (window.google && window.google.accounts && window.google.accounts.id) {
    return Promise.resolve()
  }
  if (gisScriptPromise) return gisScriptPromise
  gisScriptPromise = new Promise(function (resolve, reject) {
    const existing = document.querySelector(`script[src="${GIS_SCRIPT_SRC}"]`)
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('GIS script error')))
      if (window.google && window.google.accounts) resolve()
      return
    }
    const s = document.createElement('script')
    s.src = GIS_SCRIPT_SRC
    s.async = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('No se pudo cargar Google Identity Services'))
    document.head.appendChild(s)
  })
  return gisScriptPromise
}

class AuthGatewayModal extends Component {
  constructor(props) {
    super(props)
    const edgeSession = loadSession()
    const controllerSession = loadControllerSession()
    this.googleBtnRef = React.createRef()
    this.state = {
      tab: controllerSession || !edgeSession ? 'controller' : 'edge',
      controllerUrl: (controllerSession && controllerSession.serverUrl) || getControllerUrl(),
      controllerSession,
      meLinks: [],
      serverUrl: (edgeSession && edgeSession.serverUrl) || DEFAULT_GATEWAY_URL,
      username: (edgeSession && edgeSession.username) || '',
      password: '',
      session: edgeSession,
      loading: false,
      error: null,
      info: null,
      pushActive: false,
      pushStatus: null,
      gisReady: false,
      activeEdgeId: (() => {
        const e = loadEntitlements()
        return (e && e.edgeId) || null
      })(),
      entitlementsHint: null,
    }
  }

  /**
   * Persiste plan/shell/pacs desde links[] del Controller.
   * @param {object[]} links
   * @param {string|null} [preferredEdgeId]
   */
  applyLinksEntitlements = (links, preferredEdgeId) => {
    const preferred =
      preferredEdgeId ||
      this.state.activeEdgeId ||
      (loadEntitlements() && loadEntitlements().edgeId) ||
      null
    const result = applyControllerLinks(links, { preferredEdgeId: preferred })
    if (result.missingPlan) {
      this.setState({
        entitlementsHint: 'missing_controller_plan',
        activeEdgeId:
          (result.link && (result.link.edge_id || result.link.edgeId)) || null,
      })
      return result
    }
    if (result.entitlements) {
      this.setState({
        entitlementsHint: null,
        activeEdgeId: result.entitlements.edgeId || null,
      })
      if (typeof this.props.onEntitlementsChange === 'function') {
        this.props.onEntitlementsChange(result.entitlements)
      }
    }
    return result
  }

  handleSelectLink = (link) => {
    if (!link) return
    const edgeId = link.edge_id || link.edgeId || null
    this.setState({ activeEdgeId: edgeId }, () => {
      this.applyLinksEntitlements(this.state.meLinks || [], edgeId)
    })
  }

  componentDidMount() {
    if (this.props.open) {
      this.bootstrapOpen()
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.bootstrapOpen()
    }
    if (
      this.props.open &&
      this.state.tab === 'controller' &&
      !this.state.controllerSession &&
      this.googleBtnRef.current
    ) {
      this.renderGoogleButton()
    }
  }

  bootstrapOpen = async () => {
    const edgeSession = loadSession()
    const controllerSession = loadControllerSession()
    this.setState({
      session: edgeSession,
      controllerSession,
      controllerUrl:
        (controllerSession && controllerSession.serverUrl) ||
        this.state.controllerUrl ||
        getControllerUrl(),
      serverUrl:
        (edgeSession && edgeSession.serverUrl) ||
        this.state.serverUrl ||
        DEFAULT_GATEWAY_URL,
      username: (edgeSession && edgeSession.username) || this.state.username,
      password: '',
      error: null,
      info: null,
      pushStatus: null,
      loading: false,
      pushActive: Boolean(this.props.pushActive),
      tab: controllerSession ? 'controller' : edgeSession ? 'edge' : 'controller',
      meLinks: [],
      entitlementsHint: null,
    })
    if (controllerSession) {
      try {
        const me = await fetchMe({ session: controllerSession })
        if (!this._unmounted) {
          const links = (me && me.links) || []
          saveControllerLinks(links)
          this.setState({
            controllerSession: loadControllerSession(),
            meLinks: links,
          })
          this.applyLinksEntitlements(links)
        }
      } catch (e) {
        /* perfil opcional al abrir */
      }
    }
    this.initGis()
  }

  componentWillUnmount() {
    this._unmounted = true
  }

  initGis = async () => {
    const clientId = getGoogleClientId()
    if (!clientId) {
      this.setState({ gisReady: false })
      return
    }
    try {
      await loadGisScript()
      if (!window.google || !window.google.accounts || !window.google.accounts.id) {
        throw new Error('GIS no inicializado')
      }
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: this.handleGoogleCredential,
        auto_select: false,
        cancel_on_tap_outside: true,
      })
      this.setState({ gisReady: true }, () => this.renderGoogleButton())
    } catch (e) {
      this.setState({
        gisReady: false,
        error: (e && e.message) || String(e),
      })
    }
  }

  renderGoogleButton = () => {
    const clientId = getGoogleClientId()
    if (
      !clientId ||
      !this.state.gisReady ||
      !this.googleBtnRef.current ||
      !window.google ||
      !window.google.accounts ||
      !window.google.accounts.id
    ) {
      return
    }
    this.googleBtnRef.current.innerHTML = ''
    window.google.accounts.id.renderButton(this.googleBtnRef.current, {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      width: 280,
    })
  }

  handleGoogleCredential = async (response) => {
    const idToken = response && response.credential
    if (!idToken) {
      this.setState({ error: this.props.t('gatewayAuth.googleTokenMissing') })
      return
    }
    this.setState({ loading: true, error: null, info: null })
    try {
      const session = await loginWithGoogle(this.state.controllerUrl, idToken)
      let links = []
      try {
        const me = await fetchMe({ session })
        links = (me && me.links) || []
      } catch (e) {
        /* links opcionales */
      }
      this.setState({
        controllerSession: loadControllerSession(),
        meLinks: links,
        tab: 'controller',
      })
      saveControllerLinks(links)
      this.applyLinksEntitlements(links)
      if (typeof this.props.onControllerSessionChange === 'function') {
        this.props.onControllerSessionChange(loadControllerSession())
      }
      await this.runSubscribeController()
    } catch (e) {
      this.setState({ error: (e && e.message) || String(e) })
    } finally {
      this.setState({ loading: false })
    }
  }

  handleInputChange = (event) => {
    const { name, value } = event.target
    this.setState({ [name]: value, error: null, info: null })
  }

  notifyEdgeSession = (session) => {
    if (typeof this.props.onSessionChange === 'function') {
      this.props.onSessionChange(session)
    }
  }

  notifyPush = (active) => {
    if (typeof this.props.onPushStatusChange === 'function') {
      this.props.onPushStatusChange(Boolean(active))
    }
  }

  handleEdgeLogin = async () => {
    this.setState({ loading: true, error: null, info: null, pushStatus: null })
    try {
      const session = await login(
        this.state.serverUrl,
        this.state.username,
        this.state.password
      )
      this.setState({
        session,
        password: '',
        serverUrl: session.serverUrl,
        username: session.username,
      })
      this.notifyEdgeSession(session)
      startPresence()
      await this.runSubscribeEdge(session, session.vapidPublicKey)
    } catch (e) {
      this.setState({ error: (e && e.message) || String(e) })
    } finally {
      this.setState({ loading: false })
    }
  }

  runSubscribeController = async () => {
    const { t } = this.props
    try {
      await subscribeToPush({ preferController: true })
      this.setState({
        pushStatus: 'ok',
        pushActive: true,
        error: null,
        info: t('gatewayAuth.subscribeSuccess'),
      })
      this.notifyPush(true)
    } catch (e) {
      this.setState({
        pushStatus: 'error',
        pushActive: false,
        error: (e && e.message) || String(e),
      })
      this.notifyPush(false)
    }
  }

  runSubscribeEdge = async (session, vapidPublicKey) => {
    const { t } = this.props
    try {
      await subscribeToPush({
        session,
        vapidPublicKey,
        preferController: false,
      })
      this.setState({
        pushStatus: 'ok',
        pushActive: true,
        error: null,
        info: t('gatewayAuth.subscribeSuccess'),
      })
      this.notifyPush(true)
    } catch (e) {
      this.setState({
        pushStatus: 'error',
        pushActive: false,
        error: (e && e.message) || String(e),
      })
      this.notifyPush(false)
    }
  }

  handlePushToggle = async (event) => {
    const checked = Boolean(event.target.checked)
    if (!checked) {
      this.setState({ pushActive: false, pushStatus: null, info: null })
      this.notifyPush(false)
      return
    }
    this.setState({ loading: true, error: null, info: null, pushStatus: null })
    try {
      if (loadControllerSession()) {
        await this.runSubscribeController()
      } else {
        const session = this.state.session || loadSession()
        if (!session) {
          this.setState({
            error: this.props.t('gatewayAuth.needSession'),
            pushActive: false,
          })
          return
        }
        await this.runSubscribeEdge(session)
      }
    } finally {
      this.setState({ loading: false })
    }
  }

  handleControllerLogout = () => {
    clearControllerSession()
    this.setState({
      controllerSession: null,
      meLinks: [],
      pushActive: false,
      pushStatus: null,
      info: null,
    })
    this.notifyPush(false)
    if (typeof this.props.onControllerSessionChange === 'function') {
      this.props.onControllerSessionChange(null)
    }
  }

  handleEdgeLogout = () => {
    stopPresence()
    clearSession()
    this.setState({
      session: null,
      password: '',
      error: null,
      info: null,
      pushStatus: null,
      pushActive: false,
    })
    this.notifyPush(false)
    this.notifyEdgeSession(null)
  }

  handleStubSoon = () => {
    this.setState({
      info: this.props.t('gatewayAuth.comingSoon'),
      error: null,
    })
  }

  fieldProps = () => ({
    InputLabelProps: { style: UDV_SURFACE.muted },
    InputProps: {
      style: { color: '#e8eef4' },
    },
  })

  renderCompatBlock = () => {
    const { t, controllerCompat, gatewayCompatOk, udvVersion } = this.props
    if (!controllerCompat || !controllerCompat.ok) return null
    const below = gatewayCompatOk === false
    return (
      <div
        style={{
          marginTop: 14,
          marginBottom: 4,
          padding: '10px 12px',
          borderRadius: 8,
          border: below
            ? '1px solid rgba(255, 152, 0, 0.45)'
            : '1px solid rgba(255,255,255,0.08)',
          background: below
            ? 'rgba(255, 152, 0, 0.12)'
            : 'rgba(255,255,255,0.04)',
        }}
      >
        <Typography
          variant="body2"
          style={{
            fontWeight: 600,
            marginBottom: 6,
            color: below ? '#ffcc80' : '#e8eef4',
          }}
        >
          {below ? t('gatewayAuth.compatUpdate') : t('gatewayAuth.compatOk')}
        </Typography>
        <Typography variant="caption" style={{ display: 'block', ...UDV_SURFACE.muted }}>
          UDV {udvVersion || '?'} · min {controllerCompat.udv_min || '—'}
          {controllerCompat.edge_min
            ? ` · Edge ≥ ${controllerCompat.edge_min}`
            : ''}
          {controllerCompat.controller_version
            ? ` · Ctrl ${controllerCompat.controller_version}`
            : ''}
        </Typography>
        {Array.isArray(controllerCompat.features) &&
        controllerCompat.features.length > 0 ? (
          <Typography
            variant="caption"
            style={{ display: 'block', marginTop: 4, ...UDV_SURFACE.muted }}
          >
            {t('gatewayAuth.compatFeatures')}:{' '}
            {controllerCompat.features.join(', ')}
          </Typography>
        ) : null}
      </div>
    )
  }

  renderControllerPanel = () => {
    const { t } = this.props
    const {
      controllerUrl,
      controllerSession,
      meLinks,
      loading,
      pushActive,
      gisReady,
    } = this.state
    const user = controllerSession && controllerSession.user
    const displayName =
      (user && (user.full_name || user.email || user.id)) || t('gatewayAuth.user')

    if (controllerSession && controllerSession.token) {
      return (
        <>
          <Typography variant="body2" style={{ marginBottom: 8, ...UDV_SURFACE.muted }}>
            {t('gatewayAuth.connectedAs')}{' '}
            <strong style={{ color: '#f0f4f8' }}>{displayName}</strong>
          </Typography>
          <Typography variant="caption" style={{ display: 'block', marginBottom: 12, ...UDV_SURFACE.muted }}>
            {t('gatewayAuth.controllerSection')}
          </Typography>
          {(meLinks || []).length > 0 ? (
            <div style={{ marginBottom: 12 }}>
              <Typography variant="body2" style={{ marginBottom: 6, ...UDV_SURFACE.muted }}>
                {t('gatewayAuth.linkedClinics')}
              </Typography>
              {meLinks.map((link, idx) => {
                const edgeId = link && (link.edge_id || link.edgeId)
                const plan = link && parsePlanQueryParam(link.plan)
                const active =
                  edgeId &&
                  this.state.activeEdgeId &&
                  String(edgeId) === String(this.state.activeEdgeId)
                return (
                  <button
                    key={edgeId || idx}
                    type="button"
                    onClick={() => this.handleSelectLink(link)}
                    style={{
                      display: 'block',
                      width: '100%',
                      textAlign: 'left',
                      cursor: 'pointer',
                      marginBottom: 6,
                      padding: '8px 10px',
                      borderRadius: 8,
                      border: active
                        ? '1px solid rgba(100, 180, 255, 0.55)'
                        : '1px solid rgba(255,255,255,0.1)',
                      background: active
                        ? 'rgba(64, 140, 220, 0.18)'
                        : 'rgba(255,255,255,0.04)',
                      color: '#f0f4f8',
                      fontSize: '0.875rem',
                    }}
                  >
                    {(link && link.clinic_name) || t('gatewayAuth.noClinic')}
                    {edgeId ? ` · ${edgeId}` : ''}
                    {plan ? ` · plan ${plan}` : ''}
                    {link && (link.pacs_id || link.pacsId)
                      ? ` · ${link.pacs_id || link.pacsId}`
                      : ''}
                  </button>
                )
              })}
              {this.state.entitlementsHint === 'missing_controller_plan' ? (
                <Typography
                  variant="caption"
                  style={{
                    display: 'block',
                    marginTop: 4,
                    color: '#ffcc80',
                  }}
                >
                  {t('gatewayAuth.missingControllerPlan')}
                </Typography>
              ) : null}
            </div>
          ) : (
            <Typography variant="body2" style={{ marginBottom: 12, ...UDV_SURFACE.muted }}>
              {t('gatewayAuth.noLinks')}
            </Typography>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={!!pushActive}
                onChange={this.handlePushToggle}
                color="primary"
                disabled={loading}
              />
            }
            label={
              <span style={{ color: 'rgba(230, 237, 243, 0.92)', fontSize: '0.9rem' }}>
                {t('gatewayAuth.dutyNotifications')}
              </span>
            }
          />
        </>
      )
    }

    return (
      <>
        <TextField
          margin="dense"
          name="controllerUrl"
          label={t('gatewayAuth.controllerUrl')}
          placeholder={DEFAULT_CONTROLLER_URL}
          type="text"
          fullWidth
          value={controllerUrl}
          onChange={(e) => {
            this.handleInputChange(e)
            try {
              setControllerUrl(e.target.value)
            } catch (err) {
              /* while typing */
            }
          }}
          disabled={loading}
          autoComplete="url"
          {...this.fieldProps()}
        />
        <Typography variant="body2" style={{ marginTop: 12, marginBottom: 8, ...UDV_SURFACE.muted }}>
          {t('gatewayAuth.googleHint')}
        </Typography>
        {gisReady ? (
          <div ref={this.googleBtnRef} style={{ marginTop: 8, minHeight: 44 }} />
        ) : (
          <Typography variant="body2" style={{ color: '#ffe082', marginTop: 8 }}>
            {t('gatewayAuth.googleClientMissing')}
          </Typography>
        )}
      </>
    )
  }

  renderEdgePanel = () => {
    const { t } = this.props
    const { serverUrl, username, password, session, loading, pushActive } = this.state
    const connected = Boolean(session && session.token)

    if (connected) {
      const clinic = extractClinicName(session) || t('gatewayAuth.noClinic')
      return (
        <>
          <Typography variant="body2" style={{ marginBottom: 8, ...UDV_SURFACE.muted }}>
            {t('gatewayAuth.edgeSection')}:{' '}
            <strong style={{ color: '#f0f4f8' }}>
              {session.username || t('gatewayAuth.user')}
            </strong>
          </Typography>
          <Typography variant="body2" style={{ marginBottom: 16, ...UDV_SURFACE.muted }}>
            {t('gatewayAuth.clinic')}:{' '}
            <strong style={{ color: '#f0f4f8' }}>{clinic}</strong>
          </Typography>
          {!loadControllerSession() ? (
            <FormControlLabel
              control={
                <Switch
                  checked={!!pushActive}
                  onChange={this.handlePushToggle}
                  color="primary"
                  disabled={loading}
                />
              }
              label={
                <span style={{ color: 'rgba(230, 237, 243, 0.92)', fontSize: '0.9rem' }}>
                  {t('gatewayAuth.dutyNotifications')}
                </span>
              }
            />
          ) : null}
        </>
      )
    }

    return (
      <>
        <Typography variant="caption" style={{ display: 'block', marginBottom: 8, ...UDV_SURFACE.muted }}>
          {t('gatewayAuth.edgeLegacyHint')}
        </Typography>
        <TextField
          margin="dense"
          name="serverUrl"
          label={t('gatewayAuth.serverUrl')}
          placeholder={DEFAULT_GATEWAY_URL}
          type="text"
          fullWidth
          value={serverUrl}
          onChange={this.handleInputChange}
          disabled={loading}
          autoComplete="url"
          {...this.fieldProps()}
        />
        <TextField
          margin="dense"
          name="username"
          label={t('gatewayAuth.username')}
          type="text"
          fullWidth
          value={username}
          onChange={this.handleInputChange}
          disabled={loading}
          autoComplete="username"
          {...this.fieldProps()}
        />
        <TextField
          margin="dense"
          name="password"
          label={t('gatewayAuth.password')}
          type="password"
          fullWidth
          value={password}
          onChange={this.handleInputChange}
          disabled={loading}
          autoComplete="current-password"
          {...this.fieldProps()}
        />
      </>
    )
  }

  render() {
    const { open, onClose, t } = this.props
    const {
      tab,
      session,
      controllerSession,
      loading,
      error,
      info,
      pushStatus,
    } = this.state
    const ctrlOn = Boolean(controllerSession && controllerSession.token)
    const edgeOn = Boolean(session && session.token)

    return (
      <Dialog
        open={!!open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        {...udvDialogProps}
      >
        <DialogTitle style={UDV_SURFACE.title}>
          {ctrlOn || edgeOn
            ? t('gatewayAuth.profileTitle')
            : t('gatewayAuth.loginTitle')}
        </DialogTitle>
        <DialogContent style={UDV_SURFACE.content}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            <Button
              size="small"
              style={
                tab === 'controller'
                  ? UDV_SURFACE.btnPrimary
                  : UDV_SURFACE.btnGhost
              }
              onClick={() =>
                this.setState({ tab: 'controller', error: null }, () =>
                  this.renderGoogleButton()
                )
              }
            >
              {t('gatewayAuth.controllerTab')}
            </Button>
            <Button
              size="small"
              style={
                tab === 'edge' ? UDV_SURFACE.btnPrimary : UDV_SURFACE.btnGhost
              }
              onClick={() => this.setState({ tab: 'edge', error: null })}
            >
              {t('gatewayAuth.edgeTab')}
            </Button>
          </div>

          {tab === 'controller'
            ? this.renderControllerPanel()
            : this.renderEdgePanel()}

          {this.renderCompatBlock()}

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 16 }}>
              <CircularProgress size={18} style={{ marginRight: 10 }} />
              <Typography variant="body2" style={UDV_SURFACE.muted}>
                {ctrlOn || edgeOn
                  ? t('gatewayAuth.registeringPush')
                  : t('gatewayAuth.signingIn')}
              </Typography>
            </div>
          )}
          {error && (
            <Typography style={{ marginTop: 16, color: '#ff8a80' }}>{error}</Typography>
          )}
          {info && !error && (
            <Typography style={{ marginTop: 16, color: '#81c784' }}>{info}</Typography>
          )}
          {pushStatus === 'ok' && !error && !info && (
            <Typography style={{ marginTop: 16, color: '#81c784' }}>
              {t('gatewayAuth.subscribeSuccess')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions style={UDV_SURFACE.actions}>
          <Button onClick={onClose} style={UDV_SURFACE.btnGhost} disabled={loading}>
            {t('common.close')}
          </Button>
          {tab === 'controller' ? (
            ctrlOn ? (
              <Button
                onClick={this.handleControllerLogout}
                style={UDV_SURFACE.btnDanger}
                disabled={loading}
              >
                {t('gatewayAuth.logout')}
              </Button>
            ) : (
              <Button
                onClick={this.handleStubSoon}
                style={UDV_SURFACE.btnGhost}
                disabled={loading}
              >
                {t('gatewayAuth.register')}
              </Button>
            )
          ) : edgeOn ? (
            <Button
              onClick={this.handleEdgeLogout}
              style={UDV_SURFACE.btnDanger}
              disabled={loading}
            >
              {t('gatewayAuth.logout')}
            </Button>
          ) : (
            <Button
              onClick={this.handleEdgeLogin}
              style={UDV_SURFACE.btnPrimary}
              disabled={loading}
            >
              {t('gatewayAuth.signIn')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    )
  }
}

export default withTranslation()(AuthGatewayModal)
